import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


function MapMasterSyncTool() {
  const [mapMasterFile, setMapMasterFile] = useState(null);
  const [tabName, setTabName] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "MAP Master Sync",
    description:
      "Upload the current MAP Price Master workbook and sync a single vendor tab against Shopify.",
    steps: [
      "Upload the current MAP Price Master .xlsx file.",
      "Enter the exact name of the tab you want to sync (e.g. 'Birkenstock').",
      "Click 'Sync Master Sheet'.",
      "Save the synced workbook that downloads automatically.",
    ],
    templates: [],
  };

  const guideTips = [
    "Only the tab you name is synced against Shopify; every other tab in the workbook is copied through unchanged.",
    "The tab name match is case-insensitive but must otherwise correspond to a tab that exists in the uploaded workbook.",
    "Existing SKU rows are refreshed from Shopify; missing Shopify SKUs are appended into the matched vendor tab.",
    "Core headers are normalized to Handle, Title, Option2 Value, Variant SKU, Variant Price, and Variant Compare At Price.",
    "Non-core columns already present in the tab (Cost per item, etc.) are preserved on existing rows and left blank on new ones.",
    "Products tagged offmap in Shopify get Variant Compare At Price written as OFF.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!mapMasterFile) {
      addToast("Please upload the MAP Price Master workbook.", "error");
      return;
    }

    const trimmedTabName = tabName.trim();
    if (!trimmedTabName) {
      addToast("Please enter the tab name to sync.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (mapMasterFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("map_master_file", mapMasterFile);
    formData.append("tab_name", trimmedTabName);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/shopify/sync-map-master", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "map_price_master_synced.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      console.error(error);
      const errorMessage = await extractErrorMessage(error, "Error syncing MAP master.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="MAP Master Sync"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">MAP Price Master</h3>
              </div>
              <ToolFileUploadField
                label="MAP Price Master"
                file={mapMasterFile}
                setFile={setMapMasterFile}
                accept=".xlsx"
                helperText="Upload the current MAP Price Master .xlsx file."
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Sync Settings</h3>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Tab Name</label>
                <p className="tool-helper-text">
                  Name of the workbook tab to sync against Shopify. Case-insensitive.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Birkenstock"
                  value={tabName}
                  onChange={(event) => setTabName(event.target.value)}
                  required
                />
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={loading}>
                {loading ? "Syncing tab..." : "Sync Master Sheet"}
              </button>

              <ToolProgressPanel
                loading={loading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading MAP master..."
                processingMessage="Syncing the selected tab against Shopify..."
              />
            </div>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default MapMasterSyncTool;


